import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { FileCheck2, AlertTriangle, X, Search, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { Dialog, Input } from "./Suppliers";
import DeleteRequestButton from "../components/DeleteRequestButton";
import EditRequestButton from "../components/EditRequestButton";
import { usePager } from "../components/Pagination";
import { qtyStep, isCountUom } from "../lib/uom";
import useSubmitGuard from "../lib/useSubmitGuard";
import { fmtQty } from "../lib/format";

export default function GRN() {
  const [gateEntries, setGateEntries] = useState([]);
  const [grns, setGrns] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [pos, setPos] = useState([]);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [pendingOpen, setPendingOpen] = useState(false);
  const [createFor, setCreateFor] = useState(null);
  const [view, setView] = useState(null);
  // Wave-D (#7): Inward-Challan pending invoice queue (Store/GRN user enters invoice)
  const [pendingInvoice, setPendingInvoice] = useState({ threshold_days: 7, rows: [] });
  const [pendingInvoiceOpen, setPendingInvoiceOpen] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({});  // { [geId]: { invoice_no, invoice_date } }
  // Wave-H: pending over-receipt approvals (for badge counts + tab content)
  const [overReceipt, setOverReceipt] = useState([]);  // [{id, grn_id, material_id, status, ...}]
  const nav = useNavigate();
  const loc = useLocation();

  const load = async () => {
    const [ge, grn, s, m, p] = await Promise.all([
      api.get("/gate-entries", { params: { status: "pending_grn" } }),
      api.get("/grns"),
      api.get("/suppliers"),
      api.get("/materials"),
      api.get("/purchase-orders", { params: { status: "open" } }),
    ]);
    setGateEntries(ge.data); setGrns(grn.data); setSuppliers(s.data); setMaterials(m.data); setPos(p.data);
    const partial = await api.get("/purchase-orders", { params: { status: "partial" } });
    setPos((prev) => [...prev, ...partial.data]);
    // Wave-D (#7): inward-challan pending invoices
    try {
      const pi = await api.get("/inward-challan/pending-invoice");
      setPendingInvoice(pi.data || { threshold_days: 7, rows: [] });
    } catch { /* permission denied for some roles — silent */ }
    // Wave-H: over-receipt requests (for blocked-lines tab + GRN badges)
    try {
      const or = await api.get("/over-receipt-requests");
      setOverReceipt(or.data || []);
    } catch { setOverReceipt([]); }
    // Auto-open view when ?view=<grn_number> in URL
    const params = new URLSearchParams(loc.search);
    const wantView = params.get("view");
    if (wantView) {
      const target = grn.data.find((g) => g.number === wantView || g.id === wantView);
      if (target) setView(target);
    }
    // Deep-link from Quality ("GRN ↗"): ?grn=<number> seeds the search box
    const wantGrn = params.get("grn");
    if (wantGrn) setQ(wantGrn);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [loc.search]);

  const supName = (id) => suppliers.find((s) => s.id === id)?.name || "—";
  const matName = (id) => materials.find((m) => m.id === id)?.name || "—";
  const matCode = (id) => materials.find((m) => m.id === id)?.code || "";
  // Wave-G: id→{code,name} map for the Edit-with-approval JSON helper
  const materialsMap = React.useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, { code: m.code, name: m.name }])),
    [materials],
  );

  const blockedGrnIds = useMemo(
    () => new Set((grns || []).filter((g) => g.has_blocked_lines).map((g) => g.id)),
    [grns],
  );

  const filtered = grns.filter((g) => {
    if (tab === "pending_quality" && g.status !== "pending_quality") return false;
    if (tab === "stock_in" && !["stock_in", "deviation_accepted", "conditional"].includes(g.status)) return false;
    if (tab === "rejected" && !["pending_deviation", "rejected_return"].includes(g.status)) return false;
    if (tab === "blocked_lines" && !blockedGrnIds.has(g.id)) return false;
    if (!q.trim()) return true;
    const k = q.trim().toLowerCase();
    return (
      (g.number || "").toLowerCase().includes(k) ||
      (g.po_number || "").toLowerCase().includes(k) ||
      (g.gate_entry_number || "").toLowerCase().includes(k) ||
      (g.invoice_no || "").toLowerCase().includes(k) ||
      (supName(g.supplier_id) || "").toLowerCase().includes(k) ||
      (g.items || []).some((it) => (matCode(it.material_id) || "").toLowerCase().includes(k) || (matName(it.material_id) || "").toLowerCase().includes(k))
    );
  });
  // Wave-J pagination
  const { visibleRows: pagedRows, controls: pageControls } = usePager(filtered, 10);

  return (
    <div data-testid="grn-page">
      <PageHeader title="GRN — Goods Receipt Note" subtitle="Multi-item, PO-validated, routes directly to Quality Inspector"
        helpText="Store User creates GRN with received qty + rate. System auto-validates against linked PO. There is NO maker-checker on GRN; Quality Inspector is the GRN approver. NG → Plant Head/Admin decides deviation."
      />
      <div className="p-8 space-y-6">
        {/* Wave-D (#7): Pending Invoice queue for inward_challan GE */}
        {(pendingInvoice.rows?.length > 0) && (
          <section className="bg-white border border-[#E65100]/40 rounded-sm" data-testid="pending-invoice-section">
            <button type="button" onClick={() => setPendingInvoiceOpen((v) => !v)}
              className="w-full px-5 py-3 border-b border-[#E65100]/30 flex items-center justify-between bg-[#FFF3E0] hover:bg-[#FFE0B2] transition text-left">
              <div className="text-[10px] tracking-[0.2em] font-bold text-[#E65100] uppercase flex items-center gap-2">
                {pendingInvoiceOpen ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                <AlertTriangle size={12}/> Pending Invoice — Inward Challan ({pendingInvoice.rows.length})
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">Escalates after {pendingInvoice.threshold_days}d</span>
                <span className="text-[10px] uppercase tracking-widest text-[#E65100] font-bold">{pendingInvoiceOpen ? "Hide" : "Show"}</span>
              </div>
            </button>
            {pendingInvoiceOpen && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="pending-invoice-table">
                  <thead className="text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 bg-[#F5F5F5]">
                    <tr>
                      <th className="text-left px-4 py-2">GR No.</th>
                      <th className="text-left px-4 py-2">Challan No.</th>
                      <th className="text-left px-4 py-2">Supplier</th>
                      <th className="text-right px-4 py-2">Days pending</th>
                      <th className="text-left px-4 py-2">Invoice No.</th>
                      <th className="text-left px-4 py-2">Invoice Date</th>
                      <th className="text-left px-4 py-2">Remark *</th>
                      <th className="text-right px-4 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingInvoice.rows.map((r) => {
                      const f = invoiceForm[r.id] || {};
                      const escalated = r.escalate;
                      return (
                        <tr key={r.id} className={`border-t border-[#EEEEEE] ${escalated ? "bg-[#C62828]/5" : ""}`} data-testid={`pending-inv-row-${r.id}`}>
                          <td className="px-4 py-2 font-mono text-[#0D2B5E]">{r.number}</td>
                          <td className="px-4 py-2 font-mono text-[#E65100]">{r.challan_no}</td>
                          <td className="px-4 py-2 text-xs">{supName(r.supplier_id)}</td>
                          <td className={`px-4 py-2 text-right font-mono text-xs ${escalated ? "text-[#C62828] font-bold" : "text-[#E65100]"}`}>
                            {r.days_pending}{escalated && <span className="ml-1 text-[9px] uppercase tracking-widest">overdue</span>}
                          </td>
                          <td className="px-4 py-2">
                            <input value={f.invoice_no || ""} onChange={(e) => setInvoiceForm({ ...invoiceForm, [r.id]: { ...f, invoice_no: e.target.value } })}
                              data-testid={`enter-inv-no-${r.id}`}
                              placeholder="INV-…" className="w-32 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono"/>
                          </td>
                          <td className="px-4 py-2">
                            <input type="date" value={f.invoice_date || ""} onChange={(e) => setInvoiceForm({ ...invoiceForm, [r.id]: { ...f, invoice_date: e.target.value } })}
                              data-testid={`enter-inv-date-${r.id}`}
                              className="w-32 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono"/>
                          </td>
                          <td className="px-4 py-2">
                            <input value={f.remark || ""} onChange={(e) => setInvoiceForm({ ...invoiceForm, [r.id]: { ...f, remark: e.target.value } })}
                              data-testid={`enter-inv-remark-${r.id}`}
                              placeholder="Closing note (min 3 chars)" className="w-44 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm"/>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <button onClick={async () => {
                              if (!(f.invoice_no || "").trim()) { toast.error("Invoice number is required"); return; }
                              if ((f.remark || "").trim().length < 3) { toast.error("Closing remark is required (min 3 chars)"); return; }
                              try {
                                await api.post(`/gate-entries/${r.id}/enter-invoice`, {
                                  invoice_no: f.invoice_no.trim(),
                                  invoice_date: f.invoice_date || null,
                                  remark: f.remark.trim(),
                                });
                                toast.success(`Invoice ${f.invoice_no.trim()} linked to ${r.number}`);
                                setInvoiceForm({ ...invoiceForm, [r.id]: {} });
                                load();
                              } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
                            }} data-testid={`enter-inv-submit-${r.id}`}
                              className="bg-[#1565C0] hover:bg-[#0D47A1] text-white text-[10px] tracking-widest uppercase font-bold px-2 py-1 rounded-sm">
                              Link Invoice
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
        <section className="bg-white border border-[#EEEEEE] rounded-sm">
          <button
            type="button"
            onClick={() => setPendingOpen((v) => !v)}
            data-testid="toggle-pending-ge"
            aria-expanded={pendingOpen}
            className="w-full px-5 py-3 border-b border-[#EEEEEE] flex items-center justify-between bg-[#F5F5F5] hover:bg-[#EEEEEE] transition text-left"
          >
            <div className="text-[10px] tracking-[0.2em] font-bold text-[#0D2B5E] uppercase flex items-center gap-2">
              {pendingOpen ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
              <FileCheck2 size={12}/> Pending Gate Entries (create GRN)
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold">{pendingOpen ? "Hide" : "Show"}</span>
              <span className="text-xs text-[#0D2B5E]/60 font-mono bg-white border border-[#EEEEEE] px-2 py-0.5 rounded-sm" data-testid="pending-ge-count">{gateEntries.length}</span>
            </div>
          </button>
          {pendingOpen && (
            <div className="overflow-x-auto" data-testid="pending-ge-list">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 bg-[#F5F5F5]">
                  <tr>
                    <th className="text-left px-4 py-2">GR No.</th>
                    <th className="text-left px-4 py-2">Supplier</th>
                    <th className="text-right px-4 py-2"># Items</th>
                    <th className="text-left px-4 py-2">Invoice</th>
                    <th className="text-right px-4 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {gateEntries.map((g, i) => (
                    <tr key={g.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                      <td className="px-4 py-2 font-mono text-[#1565C0]">{g.number}</td>
                      <td className="px-4 py-2">{supName(g.supplier_id)}</td>
                      <td className="px-4 py-2 font-mono text-right">{(g.items || []).length}</td>
                      <td className="px-4 py-2 font-mono text-xs">{g.invoice_no}</td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => setCreateFor(g)} className="text-xs bg-[#1565C0] text-white px-3 py-1 rounded-sm uppercase tracking-wider font-bold" data-testid={`create-grn-${g.number}`}>Create GRN</button>
                      </td>
                  </tr>
                ))}
                {gateEntries.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-sm text-[#0D2B5E]/50">No pending gate entries.</td></tr>}
              </tbody>
            </table>
            </div>
          )}
        </section>

        <section className="bg-white border border-[#EEEEEE] rounded-sm">
          <div className="flex border-b border-[#EEEEEE] items-center">
            {[
              ["all", "All GRNs"],
              ["pending_quality", "Pending Quality"],
              ["rejected", "Rejected / Deviation"],
              ["stock_in", "Stock-In"],
              ["blocked_lines", `Blocked Lines${blockedGrnIds.size ? ` (${blockedGrnIds.size})` : ""}`],
            ].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`grn-tab-${k}`}
                className={`px-5 py-3 text-xs uppercase tracking-widest font-bold ${tab === k ? "text-[#1565C0] border-b-2 border-[#1565C0]" : "text-[#0D2B5E]/60"} ${k === "blocked_lines" && blockedGrnIds.size > 0 ? "text-[#C62828]" : ""}`}>{l}</button>
            ))}
            <div className="ml-auto px-4 py-2 relative w-80">
              <Search size={14} className="absolute left-7 top-1/2 -translate-y-1/2 text-[#0D2B5E]/40 pointer-events-none"/>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="grn-search"
                placeholder="Search GRN, PO, GE, invoice, supplier, material…"
                className="w-full pl-8 pr-3 py-2 border border-[#EEEEEE] rounded-sm text-xs bg-white focus:border-[#1565C0] outline-none"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                <tr>
                  <th className="text-left px-4 py-2">GRN</th>
                  <th className="text-left px-4 py-2">PO</th>
                  <th className="text-left px-4 py-2">Supplier</th>
                  <th className="text-right px-4 py-2"># Items</th>
                  <th className="text-left px-4 py-2">Mismatch</th>
                  <th className="text-left px-4 py-2">QC</th>
                  <th className="text-left px-4 py-2">Maker</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((g, i) => {
                  const totalItems = (g.items || []).length;
                  const blockedCount = g.blocked_lines_count || (g.items || []).filter((it) => it.line_status === "pending_over_receipt_approval").length;
                  const stockedCount = Math.max(0, totalItems - blockedCount);
                  return (
                  <tr key={g.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                    <td className="px-4 py-2 font-mono text-[#1565C0]">
                      <button onClick={() => setView(g)} className="hover:underline font-bold" data-testid={`grn-link-${g.number}`}>{g.number}</button>
                      {blockedCount > 0 && (
                        <div className="mt-0.5 text-[9px] uppercase tracking-widest text-[#C62828] font-bold bg-[#C62828]/10 inline-block px-1.5 py-0.5 rounded-sm" data-testid={`grn-blocked-badge-${g.number}`}>
                          {stockedCount} of {totalItems} stocked · {blockedCount} pending over-receipt approval
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{g.po_number || "—"}</td>
                    <td className="px-4 py-2">{supName(g.supplier_id)}</td>
                    <td className="px-4 py-2 font-mono text-right">{totalItems}</td>
                    <td className="px-4 py-2 text-xs">
                      {g.qty_mismatch && <span className="text-[#E65100] font-bold mr-1">QTY</span>}
                      {g.rate_mismatch && <span className="text-[#C62828] font-bold">RATE</span>}
                      {!g.qty_mismatch && !g.rate_mismatch && <span className="text-[#2E7D32]">—</span>}
                    </td>
                    {/* Live-Prod-Fix #3 — GRN <-> QC cross-link column */}
                    <td className="px-4 py-2 text-xs">
                      {g.qc_skipped ? (
                        <span className="text-[#0D2B5E]/50" title="QC skipped (all items non-QC)">— skipped</span>
                      ) : (
                        <a
                          href={`/quality?grn=${encodeURIComponent(g.number)}`}
                          data-testid={`grn-open-qc-${g.number}`}
                          title="Open linked Quality Report"
                          className="uppercase tracking-widest border border-[#1565C0] text-[#1565C0] px-1.5 py-0.5 rounded-sm hover:bg-[#1565C0] hover:text-white transition text-[9px] font-bold"
                        >QC ↗</a>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs">{g.maker_name}</td>
                    <td className="px-4 py-2"><StatusBadge status={g.status}/></td>
                    <td className="px-4 py-2 text-right space-x-1">
                      {["stock_in", "deviation_accepted", "conditional"].includes(g.status) && (
                        <button onClick={() => nav(`/stock-in/${g.id}`)} className="text-xs bg-[#1565C0] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold" data-testid={`stickers-${g.number}`}>Stickers</button>
                      )}
                      {/* Feb-2026 #2 — once a GRN reaches stock_in the
                          stickers are generated. Editing afterwards would
                          desync stickers from ledger, so the edit button is
                          hidden. Pre-stock-in states still allow PH/SA-approved
                          edits. */}
                      {["deviation_accepted", "conditional", "pending_deviation"].includes(g.status) && (
                        <span className="inline-block align-middle"><EditRequestButton
                          module="grn" record={g} label={g.number}
                          materialsMap={materialsMap}
                          materials={materials}
                          fields={[
                            { key: "remarks", label: "Remarks", type: "textarea" },
                            { key: "invoice_no", label: "Invoice No.", type: "text" },
                            { key: "invoice_qty", label: "Invoice Qty (total)", type: "number" },
                            { key: "items", label: "Line Items", type: "items_grn" },
                          ]}
                          onSubmitted={load}/></span>
                      )}
                      <span className="inline-block align-middle"><DeleteRequestButton module="grn" record={g} label={g.number} onDeleted={load}/></span>
                    </td>
                  </tr>
                  );
                })}
                {filtered.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-sm text-[#0D2B5E]/50">No GRNs in this view.</td></tr>}
              </tbody>
            </table>
            {pageControls}
          </div>
        </section>
      </div>

      {createFor && (
        <CreateGRN entry={createFor} onClose={() => setCreateFor(null)} onDone={() => { setCreateFor(null); load(); }}
          supName={supName} matLabel={(id) => `${matCode(id)} — ${matName(id)}`}
          pos={pos.filter((p) => p.supplier_id === createFor.supplier_id)}/>
      )}
      {view && <ViewGRN grn={view} onClose={() => setView(null)} matLabel={(id) => `${matCode(id)} — ${matName(id)}`} supName={supName}/>}
    </div>
  );
}

function CreateGRN({ entry, onClose, onDone, supName, matLabel, pos }) {
  // Feb-2026 — submit guard prevents double-tap duplicates on tablets.
  const { busy: submitBusy, guard: submitGuard, reset: resetGuard } = useSubmitGuard();
  const [poId, setPoId] = useState("");
  const [materialsCache, setMaterialsCache] = useState({});
  const [defaultAssetLocation, setDefaultAssetLocation] = useState("");
  const [items, setItems] = useState((entry.items || []).map((it) => ({
    material_id: it.material_id,
    accepted_packing_qty: it.packing_boxes,
    rate: 0,
    // Live-Prod-Fix follow-up: track which source filled the rate so the
    // Store User sees it before saving. "manual" once the user edits.
    rate_source: null,
    rate_touched: false,
    // One qty input per box (box_no = index+1). Empty string = not yet entered.
    box_qtys: Array.from({ length: it.packing_boxes || 1 }, () => ""),
    // Feb-2026 — Asset GRN routing. Filled lazily once materials load and we
    // know the row's category. Stays [] for non-asset materials.
    asset_numbers: [],
  })));
  const [invoiceQty, setInvoiceQty] = useState("");
  const [remarks, setRemarks] = useState("");

  // Total received_qty per item is auto-summed from box_qtys.
  const itemTotal = (it) => (it.box_qtys || []).reduce((a, v) => a + (+v || 0), 0);
  const allBoxesFilled = (it) =>
    (it.box_qtys || []).length > 0 && (it.box_qtys || []).every((v) => v !== "" && v !== null && Number(v) >= 0);
  // Feb-2026 — helpers for Asset GRN routing.
  const isAssetItem = (it) => (materialsCache[it.material_id]?.category || "").toLowerCase() === "asset";
  const assetSlotsExpected = (it) => Math.max(0, Math.floor(itemTotal(it)));
  const allAssetNumbersFilled = (it) => {
    if (!isAssetItem(it)) return true;
    const need = assetSlotsExpected(it);
    if (need <= 0) return false;
    const slots = (it.asset_numbers || []).slice(0, need).map((n) => (n || "").trim().toUpperCase());
    if (slots.length !== need || slots.some((n) => !n)) return false;
    const ASSET_RE = /^[A-Z0-9-]{3,30}$/;
    if (!slots.every((n) => ASSET_RE.test(n))) return false;
    return new Set(slots).size === slots.length;
  };
  const canSubmit = items.every(allBoxesFilled)
    && items.every((it) => itemTotal(it) > 0)
    && items.every(allAssetNumbersFilled);

  // User msg #667 #1 — unit-aware quantity precision. Drives <input step> and
  // client-side validation. Returns the UOM string for the given item row.
  const itemUom = (it) => materialsCache[it.material_id]?.uom || "";
  // For count UOMs we reject decimals (must equal Math.floor). For weight/
  // length/volume we accept up to 3 decimals via the standard <input step>.
  const isDecimalInvalid = (val, uom) => {
    if (val === "" || val === null || val === undefined) return false;
    const n = Number(val);
    if (!Number.isFinite(n)) return true;
    if (isCountUom(uom) && n !== Math.floor(n)) return true;
    if (!isCountUom(uom)) {
      // Round-trip to 3 decimals; reject anything beyond.
      const rounded = Math.round(n * 1000) / 1000;
      if (Math.abs(rounded - n) > 1e-9) return true;
    }
    return false;
  };

  // Live-Prod-Fix follow-up — rate source cascade helper. Mirrors the
  // backend resolution order: PO > Gate Entry (value/qty) > Material cost.
  const geRateFor = (mid) => {
    const geIt = (entry.items || []).find((x) => x.material_id === mid) || {};
    const q = Number(geIt.qty) || 0;
    const v = Number(geIt.value) || 0;
    return q > 0 && v > 0 ? v / q : 0;
  };

  // Auto-fill rate: Gate Entry (value/qty) first, then material master cost.
  useEffect(() => {
    api.get("/materials").then((r) => {
      const map = {};
      r.data.forEach((m) => { map[m.id] = m; });
      setMaterialsCache(map);
      setItems((arr) => arr.map((it) => {
        if (it.rate_touched || (+it.rate || 0) > 0) return it;
        const geRate = geRateFor(it.material_id);
        if (geRate > 0) return { ...it, rate: geRate, rate_source: "gate_entry" };
        const mcost = Number(map[it.material_id]?.cost) || 0;
        if (mcost > 0) return { ...it, rate: mcost, rate_source: "material" };
        return { ...it, rate_source: null };
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedPo = pos.find((p) => p.id === poId);

  // Auto-fill rate from PO when PO selected (highest priority, only if the
  // user hasn't manually typed a rate for that row).
  useEffect(() => {
    if (!selectedPo) return;
    setItems((arr) => arr.map((it) => {
      if (it.rate_touched) return it;
      const poItem = selectedPo.items.find((x) => x.material_id === it.material_id);
      return poItem && (+poItem.rate || 0) > 0 ? { ...it, rate: poItem.rate, rate_source: "po" } : it;
    }));
  }, [selectedPo]);

  const updateItem = (i, patch) => setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));

  // Feb-2026 — Asset GRN routing. Instead of mirroring asset_numbers length
  // via useEffect (which fights the linter and the React render cycle), we
  // derive the visible slots from box_qtys at render time and only persist
  // edits to non-empty entries. `asset_numbers[i]` is `undefined` until the
  // operator types into slot `i`.
  const getAssetSlots = (it) => {
    const need = Math.max(0, Math.floor((it.box_qtys || []).reduce((a, v) => a + (+v || 0), 0)));
    const arr = it.asset_numbers || [];
    return Array.from({ length: need }, (_, i) => arr[i] ?? "");
  };
  const setAssetSlot = (i, ni, val) => {
    const it = items[i];
    const slots = getAssetSlots(it);
    slots[ni] = val.toUpperCase();
    updateItem(i, { asset_numbers: slots });
  };

  const itemMismatch = (it) => {
    if (!selectedPo) return null;
    const poItem = selectedPo.items.find((x) => x.material_id === it.material_id);
    if (!poItem) return { kind: "no_po_item", text: "Material not in PO" };
    const issues = [];
    if (Math.abs((+it.rate || 0) - poItem.rate) > 0.01) issues.push(`Rate ≠ PO ₹${poItem.rate}`);
    if (itemTotal(it) !== poItem.qty) issues.push(`Qty: PO ${poItem.qty} vs received ${itemTotal(it)}`);
    return issues.length ? { kind: "mismatch", text: issues.join(" · ") } : null;
  };

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Fill received qty for every box before submitting.");
      return;
    }
    // User msg #667 #1 — unit-aware precision check (client-side; server also enforces)
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const uom = itemUom(it);
      for (let bi = 0; bi < (it.box_qtys || []).length; bi++) {
        if (isDecimalInvalid(it.box_qtys[bi], uom)) {
          toast.error(`Box ${bi + 1}: ${isCountUom(uom) ? "whole numbers only for count units (NOS/PCS)." : "max 3 decimals (kg/mtr/ltr)."}`);
          return;
        }
      }
    }
    try {
      // Live-Prod-Fix follow-up — block early if any line has no rate and no
      // fallback was found (the server would 400 anyway; this is faster).
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if ((+it.rate || 0) <= 0) {
          toast.error(`${matLabel(it.material_id)}: rate is 0 and no fallback (PO / Gate Entry / Material master) was found. Enter the rate manually.`);
          return;
        }
      }
      await submitGuard(async (idemKey) => {
        await api.post("/grns", {
          gate_entry_id: entry.id,
          po_id: poId || null,
          items: items.map((it) => ({
            material_id: it.material_id,
            received_qty: itemTotal(it),
            accepted_packing_qty: +it.accepted_packing_qty,
            rate: +it.rate,
            box_qtys: (it.box_qtys || []).map((v) => +v || 0),
            // Feb-2026 — only include asset_numbers for asset rows
            asset_numbers: isAssetItem(it)
              ? getAssetSlots(it).map((n) => (n || "").trim().toUpperCase())
              : undefined,
          })),
          invoice_qty: invoiceQty ? +invoiceQty : null,
          remarks,
          default_asset_location: defaultAssetLocation || "",
        }, { headers: { "Idempotency-Key": idemKey } });
      });
      toast.success("GRN created — routed to Quality Inspector");
      resetGuard();
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Dialog title={`Create GRN against ${entry.number}`} onClose={onClose} wide>
      <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
        <KV k="Supplier" v={supName(entry.supplier_id)}/>
        <KV k="Invoice" v={entry.invoice_no}/>
        <div>
          <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Linked PO (optional)</label>
          <select value={poId} onChange={(e) => setPoId(e.target.value)} data-testid="grn-po" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
            <option value="">— No PO —</option>
            {pos.map((p) => <option key={p.id} value={p.id}>{p.number} ({p.status})</option>)}
          </select>
        </div>
      </div>

      <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60 mb-1">Items · Per-box quantity entry</div>
      <div className="text-[11px] text-[#0D2B5E]/60 mb-3 leading-relaxed">
        Security recorded {items.reduce((a, it) => a + (+it.accepted_packing_qty || 0), 0)} box{items.reduce((a, it) => a + (+it.accepted_packing_qty || 0), 0) === 1 ? "" : "es"} and a total of <span className="font-mono font-bold text-[#0D2B5E]">{fmtQty(entry.total_qty != null ? entry.total_qty : (entry.items || []).reduce((s, it) => s + (Number(it.qty) || 0), 0))}</span> qty for this gate entry. Enter the received quantity for every box below — the GRN total auto-sums and the backend hard-blocks submission unless <strong>per-line received_qty = Gate-Entry qty</strong> and (if entered) <strong>Invoice Qty (Total) = GE Total Qty</strong>.
      </div>
      <div className="space-y-4 mb-5">
        {items.map((it, idx) => {
          const mismatch = itemMismatch(it);
          const geItem = (entry.items || []).find((x) => x.material_id === it.material_id) || {};
          const expected = geItem.expected_qty;
          const geQty = Number(geItem.qty || 0);  // Feb-2026 — GE-qty target
          const recv = itemTotal(it);
          const geMatches = Math.abs(recv - geQty) < 0.001 && recv > 0;
          let schedVar = null;
          if (expected != null) {
            if (expected === 0 && recv > 0) schedVar = { level: "excess", text: `Excess ${recv} (no schedule)` };
            else if (expected > 0) {
              const diff = recv - expected;
              const pct = Math.abs(diff) / Math.max(expected, 1) * 100;
              if (pct > 10) schedVar = { level: diff < 0 ? "short" : "excess", text: `${diff < 0 ? "Short" : "Excess"} ${Math.abs(diff)} (${pct.toFixed(0)}%) · expected ${expected}` };
              else schedVar = { level: "ok", text: `OK · expected ${expected}` };
            }
          } else { schedVar = { level: "warn", text: "Unscheduled material" }; }
          return (
            <div key={idx} className="border border-[#EEEEEE] rounded-sm" data-testid={`grn-item-${idx}`}>
              <div className="bg-[#F5F5F5] px-4 py-2 flex items-center justify-between">
                <div className="text-sm font-bold text-[#0D2B5E]">{matLabel(it.material_id)}</div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-[#0D2B5E]/60 uppercase tracking-widest font-bold">GE qty:</span>
                  <span className="font-mono text-[#0D2B5E]" data-testid={`grn-ge-qty-${idx}`}>{fmtQty(geQty)}</span>
                  <span className="text-[#0D2B5E]/60 uppercase tracking-widest font-bold ml-3">Boxes:</span>
                  <span className="font-mono text-[#0D2B5E]">{it.accepted_packing_qty}</span>
                  <span className="text-[#0D2B5E]/60 uppercase tracking-widest font-bold ml-3">Received:</span>
                  <span className={`font-mono font-bold text-base ${geMatches ? "text-[#2E7D32]" : (recv > 0 ? "text-[#C62828]" : "text-[#0D2B5E]")}`} data-testid={`grn-item-total-${idx}`}>{fmtQty(recv)}</span>
                  {recv > 0 && !geMatches && <span className="text-[10px] uppercase tracking-widest font-bold bg-[#C62828]/15 text-[#C62828] px-2 py-0.5 rounded-sm">≠ GE qty</span>}
                  <span className="text-[#0D2B5E]/60 uppercase tracking-widest font-bold ml-3">Rate:</span>
                  <input type="number" step="0.01" value={it.rate} onChange={(e) => updateItem(idx, { rate: e.target.value, rate_touched: true, rate_source: "manual" })} data-testid={`grn-item-rate-${idx}`}
                    className="w-24 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono text-right"/>
                  {/* Live-Prod-Fix follow-up — show which fallback filled the
                      rate (or that the user typed it manually). */}
                  {it.rate_source === "po" && <span className="text-[9px] uppercase tracking-widest font-bold bg-[#1565C0]/10 text-[#1565C0] px-1.5 py-0.5 rounded-sm" data-testid={`grn-rate-src-${idx}`}>from PO</span>}
                  {it.rate_source === "gate_entry" && <span className="text-[9px] uppercase tracking-widest font-bold bg-[#00897B]/10 text-[#00897B] px-1.5 py-0.5 rounded-sm" data-testid={`grn-rate-src-${idx}`}>from GE</span>}
                  {it.rate_source === "material" && <span className="text-[9px] uppercase tracking-widest font-bold bg-[#E65100]/10 text-[#E65100] px-1.5 py-0.5 rounded-sm" data-testid={`grn-rate-src-${idx}`}>from Material</span>}
                  {it.rate_source === "manual" && <span className="text-[9px] uppercase tracking-widest font-bold bg-[#0D2B5E]/10 text-[#0D2B5E] px-1.5 py-0.5 rounded-sm" data-testid={`grn-rate-src-${idx}`}>Manual</span>}
                  {!it.rate_source && (+it.rate || 0) <= 0 && <span className="text-[9px] uppercase tracking-widest font-bold bg-[#C62828]/15 text-[#C62828] px-1.5 py-0.5 rounded-sm" data-testid={`grn-rate-src-${idx}`}>Required</span>}
                </div>
              </div>
              <div className="px-3 pt-3 pb-1 flex items-center gap-2 text-xs border-b border-dashed border-[#EEEEEE]">
                <span className="text-[#0D2B5E]/60 uppercase tracking-widest font-bold">Same qty for all {it.accepted_packing_qty} boxes:</span>
                <input type="number" min="0" step={qtyStep(itemUom(it))} placeholder={`qty/box${isCountUom(itemUom(it)) ? "" : " (3-dec ok)"}`}
                  value={it._fillVal ?? ""}
                  onChange={(e) => updateItem(idx, { _fillVal: e.target.value === "" ? "" : Math.max(0, +e.target.value) })}
                  className="w-24 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono text-right"
                  data-testid={`grn-fill-input-${idx}`}/>
                <button type="button" onClick={() => {
                  const v = it._fillVal;
                  if (v === "" || v === null || v === undefined) return;
                  if (isDecimalInvalid(v, itemUom(it))) {
                    toast.error(`${itemUom(it).toUpperCase()}: ${isCountUom(itemUom(it)) ? "whole numbers only for count units" : "max 3 decimals"}`);
                    return;
                  }
                  updateItem(idx, { box_qtys: it.box_qtys.map(() => v) });
                }} disabled={it._fillVal === "" || it._fillVal === null || it._fillVal === undefined}
                  className="text-xs bg-[#1565C0] hover:bg-[#0D2B5E] disabled:bg-[#BBB] text-white px-3 py-1 rounded-sm uppercase tracking-wider font-bold"
                  data-testid={`grn-fill-all-${idx}`}>Fill all</button>
                <button type="button" onClick={() => updateItem(idx, { box_qtys: it.box_qtys.map(() => "") })}
                  className="text-xs text-[#C62828] hover:underline ml-auto"
                  data-testid={`grn-fill-clear-${idx}`}>Clear all</button>
              </div>
              <div className="p-3 grid grid-cols-6 gap-2">
                {(it.box_qtys || []).map((bq, bi) => {
                  const uom = itemUom(it);
                  const bad = isDecimalInvalid(bq, uom);
                  return (
                  <div key={bi} className="flex flex-col" data-testid={`grn-box-${idx}-${bi + 1}`}>
                    <label className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/55 font-bold mb-0.5">Box {bi + 1}</label>
                    <input type="number" min="0" step={qtyStep(uom)} value={bq}
                      onChange={(e) => {
                        const next = [...it.box_qtys];
                        next[bi] = e.target.value === "" ? "" : Math.max(0, +e.target.value);
                        updateItem(idx, { box_qtys: next });
                      }}
                      placeholder={isCountUom(uom) ? "qty" : "qty (dec ok)"}
                      title={bad ? (isCountUom(uom) ? "Whole numbers only for count units." : "Max 3 decimal places.") : ""}
                      className={`border px-2 py-1.5 text-sm rounded-sm font-mono text-right ${bad ? "border-[#C62828] bg-[#C62828]/10" : bq === "" ? "border-[#C62828] bg-[#C62828]/5" : "border-[#EEEEEE]"}`}
                      data-testid={`grn-box-qty-${idx}-${bi + 1}`}/>
                  </div>
                  );
                })}
              </div>
              {/* Feb-2026 — Asset GRN routing: capture one asset number per unit. */}
              {isAssetItem(it) && (
                <div className="px-3 pb-3" data-testid={`grn-asset-block-${idx}`}>
                  <div className="border-t border-dashed border-[#FFB74D] pt-3 mt-1">
                    <div className="text-[10px] tracking-widest uppercase font-bold text-[#E65100] mb-2 flex items-center gap-2">
                      <span className="bg-[#E65100] text-white px-1.5 py-0.5 rounded-sm">ASSET</span>
                      Asset numbers ({getAssetSlots(it).filter((n) => (n || "").trim()).length} of {assetSlotsExpected(it)} required) ·
                      <span className="font-normal normal-case tracking-normal text-[#0D2B5E]/60">3–30 chars: A-Z, 0-9, hyphen. Numbers are reserved forever — never reused.</span>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {getAssetSlots(it).map((n, ni) => {
                        const norm = (n || "").trim().toUpperCase();
                        const bad = norm.length > 0 && !/^[A-Z0-9-]{3,30}$/.test(norm);
                        return (
                          <div key={ni} className="flex flex-col" data-testid={`grn-asset-slot-${idx}-${ni + 1}`}>
                            <label className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/55 font-bold mb-0.5">Unit {ni + 1}</label>
                            <input type="text" value={n || ""}
                              onChange={(e) => setAssetSlot(idx, ni, e.target.value)}
                              placeholder="ASSET-001"
                              className={`border px-2 py-1.5 text-sm rounded-sm font-mono uppercase ${bad ? "border-[#C62828] bg-[#C62828]/10" : (norm === "" ? "border-[#C62828] bg-[#C62828]/5" : "border-[#EEEEEE]")}`}
                              data-testid={`grn-asset-no-${idx}-${ni + 1}`}/>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
              <div className="px-4 pb-3 flex items-center justify-between text-xs">
                <div data-testid={`grn-sched-${idx}`}>
                  {schedVar?.level === "ok" ? <span className="text-[#2E7D32]">{schedVar.text}</span>
                    : schedVar?.level === "warn" ? <span className="text-[#E65100] flex items-center gap-1"><AlertTriangle size={12}/>{schedVar.text}</span>
                    : <span className="text-[#C62828] flex items-center gap-1 font-bold"><AlertTriangle size={12}/>{schedVar.text}</span>}
                </div>
                <div>
                  {selectedPo ? (
                    mismatch ? <span className="text-[#E65100] flex items-center gap-1"><AlertTriangle size={12}/>{mismatch.text}</span>
                             : <span className="text-[#2E7D32]">PO match ✓</span>
                  ) : <span className="text-[#0D2B5E]/40">No PO linked</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Input label="Invoice Qty (total)" type="number" step="0.001" value={invoiceQty} onChange={setInvoiceQty}/>
        <Input label="Default Asset Location" value={defaultAssetLocation} onChange={setDefaultAssetLocation} testId="grn-default-asset-location"
               placeholder={items.some(isAssetItem) ? "e.g. Plant Floor A (defaults to Receiving Bay)" : "(only used for asset items)"}/>
        <Input label="Remarks" value={remarks} onChange={setRemarks}/>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button onClick={onClose} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
        <button onClick={submit} disabled={!canSubmit || submitBusy}
          className="px-4 py-2 bg-[#1565C0] disabled:bg-[#BBB] text-white text-sm rounded-sm font-semibold tracking-wider uppercase disabled:cursor-not-allowed"
          data-testid="submit-grn-btn">
          {submitBusy ? "Submitting…" : (canSubmit ? "Submit to Quality" : "Fill all boxes to submit")}
        </button>
      </div>
    </Dialog>
  );
}

function ViewGRN({ grn, onClose, matLabel, supName }) {
  const [overReq, setOverReq] = React.useState([]);
  React.useEffect(() => {
    api.get(`/over-receipt-requests/by-grn/${grn.id}`)
      .then((r) => setOverReq(r.data || []))
      .catch(() => setOverReq([]));
  }, [grn.id]);
  const blockedItems = (grn.items || []).filter((it) => it.line_status === "pending_over_receipt_approval");
  return (
    <Dialog title={`GRN ${grn.number}`} onClose={onClose} wide>
      <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
        <KV k="Supplier" v={supName(grn.supplier_id)}/>
        <KV k="Gate Entry" v={grn.gate_entry_number}/>
        <KV k="PO" v={grn.po_number || "—"}/>
        {/* Wave-H Dual Date — both dates always shown */}
        <KV k="Gate Entry Date" v={grn.gate_entry_date || "—"}/>
        <KV k="Invoice Date" v={grn.invoice_date || <span className="text-[#E65100] font-bold">PENDING</span>}/>
        <KV k="Invoice No." v={grn.invoice_no || <span className="text-[#E65100] font-bold">PENDING</span>}/>
        <KV k="Maker" v={grn.maker_name}/>
        <KV k="Status" v={grn.status}/>
      </div>

      {/* Wave-H: Blocked Lines summary */}
      {blockedItems.length > 0 && (
        <div className="bg-[#C62828]/5 border-2 border-[#C62828]/40 rounded-sm p-3 mb-4" data-testid="grn-blocked-summary">
          <div className="text-[10px] tracking-widest uppercase font-bold text-[#C62828] mb-1 flex items-center gap-2">
            <AlertTriangle size={12}/> Blocked Lines — Pending Over-Receipt Approval ({blockedItems.length})
          </div>
          <div className="text-[11px] text-[#0D2B5E]/70 leading-relaxed mb-2">
            These lines crossed the 150% schedule threshold. Stickers are NOT issued and stock is NOT incremented until Plant Head / Super Admin approves.
          </div>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 bg-[#C62828]/5">
              <tr><th className="text-left px-2 py-1">Material</th><th className="text-right px-2 py-1">Received</th><th className="text-left px-2 py-1">Reason</th><th className="text-left px-2 py-1">Status</th></tr>
            </thead>
            <tbody>
              {blockedItems.map((it, i) => {
                const reqRow = overReq.find((r) => r.material_id === it.material_id && r.status === "pending");
                return (
                  <tr key={i} className="border-t border-[#C62828]/20">
                    <td className="px-2 py-1 font-mono">{matLabel(it.material_id)}</td>
                    <td className="px-2 py-1 font-mono text-right">{fmtQty(it.received_qty)}</td>
                    <td className="px-2 py-1 text-[#C62828]">{it.block_reason || "—"}</td>
                    <td className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold">
                      {reqRow ? <span className="text-[#E65100]">Pending PH/SA</span> : <span className="text-[#0D2B5E]/40">No request</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <table className="w-full text-sm border border-[#EEEEEE]">
        <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
          <tr><th className="text-left px-2 py-1">Material</th><th className="text-right px-2 py-1">Recv Qty</th><th className="text-right px-2 py-1">PO Qty</th><th className="text-right px-2 py-1">Rate</th><th className="text-right px-2 py-1">PO Rate</th><th className="text-left px-2 py-1">Period / Variance</th><th className="text-left px-2 py-1">Flags</th></tr>
        </thead>
        <tbody>
          {(grn.items || []).map((it, i) => {
            const vb = it.variance_block || {};
            return (
            <tr key={i} className={i % 2 === 0 ? "" : "bg-[#F5F5F5]/40"}>
              <td className="px-2 py-1">{matLabel(it.material_id)}</td>
              <td className="px-2 py-1 font-mono text-right">{fmtQty(it.received_qty)}</td>
              <td className="px-2 py-1 font-mono text-right">{it.po_qty ?? "—"}</td>
              <td className="px-2 py-1 font-mono text-right">
                {fmtQty(it.rate)}
                {/* Live-Prod-Fix follow-up — rate provenance on saved GRNs */}
                {it.rate_source && (
                  <span className={`ml-1 text-[9px] uppercase tracking-widest font-bold px-1 py-0.5 rounded-sm ${
                    it.rate_source === "po" ? "bg-[#1565C0]/10 text-[#1565C0]"
                    : it.rate_source === "gate_entry" ? "bg-[#00897B]/10 text-[#00897B]"
                    : it.rate_source === "material" ? "bg-[#E65100]/10 text-[#E65100]"
                    : "bg-[#0D2B5E]/10 text-[#0D2B5E]"
                  }`} data-testid={`grn-view-rate-src-${i}`}>{it.rate_source.replace("_", " ")}</span>
                )}
              </td>
              <td className="px-2 py-1 font-mono text-right">{it.po_rate ?? "—"}</td>
              <td className="px-2 py-1 text-[10px]">
                {vb.no_schedule ? <span className="text-[#0D2B5E]/50">No schedule set</span> :
                  vb.zero_schedule ? <span className="text-[#E65100]">No scheduled qty</span> : (
                    <div>
                      <div className="font-mono">{vb.period_label}</div>
                      <div className="font-mono">Sched {vb.scheduled_qty} · Recv {vb.period_received_total}</div>
                      {vb.variance != null && (
                        <div className={`font-mono font-bold ${vb.over_receipt_flag ? "text-[#C62828]" : (vb.variance < 0 ? "text-[#0D2B5E]/60" : "text-[#2E7D32]")}`}>
                          {vb.variance > 0 ? "+" : ""}{vb.variance} ({vb.variance_pct}%)
                          {vb.over_receipt_flag && (
                            <div className="text-[9px] uppercase tracking-widest">
                              Over-receipt vs schedule{vb.pending_approval ? " · Pending PH/SA approval" : ""}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                }
              </td>
              <td className="px-2 py-1 text-[10px] uppercase tracking-widest">
                {it.line_status === "pending_over_receipt_approval" && <span className="text-[#C62828] font-bold">Blocked</span>}
                {it.line_status === "rejected_over_receipt" && <span className="text-[#C62828]">Rejected</span>}
                {it.qty_mismatch && <span className="text-[#E65100] mr-1">qty-{it.qty_mismatch}</span>}
                {it.rate_mismatch && <span className="text-[#C62828]">rate-mismatch</span>}
                {!it.qty_mismatch && !it.rate_mismatch && !it.line_status?.includes("over_receipt") && <span className="text-[#2E7D32]">OK</span>}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
      {grn.remarks && <div className="text-xs mt-3"><span className="font-bold text-[#0D2B5E]/60">Remarks:</span> {grn.remarks}</div>}
    </Dialog>
  );
}

function KV({ k, v }) {
  return (
    <div className="border-l-2 border-[#1565C0] pl-3">
      <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/50 font-bold">{k}</div>
      <div className="text-sm font-medium text-[#0D2B5E]">{v}</div>
    </div>
  );
}
